import BaseAPI from "./BaseAPI";
import axios from "axios";
import { getAPIUrl, getAPIUrlV2 } from "../apiConfig";
import { haccpPlanRoute } from "../routes";
import { normalizeAPIError } from "@/services/apiErrors";

/**
 * HACCP plan API client.
 *
 * Multi-plan-per-tenant. Plans are Codex-shaped (typed sub-documents for
 * team / products / flow / hazards / CCPs / verification / records),
 * with a snapshot-on-publish revision model. CCP monitoring procedures
 * link to existing CheckForms.
 */
export default class HACCPPlanAPI extends BaseAPI {
    constructor(emitterInstance) {
        super(emitterInstance, haccpPlanRoute, "haccp-plan");
    }

    /**
     * Create a plan, optionally seeded from a template.
     * @param {Object} data - { name, scope, segment, location_ids, owner_user_id }
     * @param {string} [templateId] - "cafe-sfbb", "manufacturer-small", or "blank"
     */
    async Create(data, templateId = null) {
        const url = templateId
            ? this._url(`?template_id=${encodeURIComponent(templateId)}`)
            : this._url();
        const result = await this._request("post", url, data || {});
        this._emit("add", result.id);
        return result;
    }

    /** Fetch a single plan by id. */
    async Get(id) {
        return this._request("get", this._url(`/${id}`));
    }

    /** List plans, optionally filtered by status or segment. */
    async List(params = {}) {
        const query = new URLSearchParams();
        if (params.status) query.set("status", params.status);
        if (params.segment) query.set("segment", params.segment);
        const suffix = query.toString() ? `?${query}` : "";
        return this._request("get", this._url(suffix));
    }

    /** Update a draft plan via JSON Merge Patch. */
    async Update(id, patch) {
        const result = await this._request("put", this._url(`/${id}`), patch);
        this._emit("update", id);
        return result;
    }

    /** Archive an active plan (no replacement). */
    async Archive(id) {
        const result = await this._request("post", this._url(`/${id}/archive`));
        this._emit("archive", id);
        return result;
    }

    /** Duplicate a plan into a new draft. Preserves family if source was active. */
    async Duplicate(id) {
        const result = await this._request("post", this._url(`/${id}/duplicate`));
        this._emit("duplicate", id);
        return result;
    }

    /** Publish a draft: snapshot the current active (if any), promote draft to active, append revision. */
    async PublishRevision(id, { reason, summary, signature }) {
        const result = await this._request("post", this._url(`/${id}/publish`), { reason, summary, signature });
        this._emit("publish", id);
        return result;
    }

    /** Get the per-principle completeness report (publish gate). */
    async Completeness(id) {
        return this._request("get", this._url(`/${id}/completeness`));
    }

    /** List all revisions in a family (all status). */
    async ListRevisionsByFamily(familyId) {
        return this._request("get", this._url(`/family/${familyId}`));
    }

    /** Link a CCP's monitoring to a check form, optionally with a field-scope pointer. */
    async LinkCCPToCheck(planId, ccpId, { check_form_id, field_scope }) {
        const result = await this._request(
            "put",
            this._url(`/${planId}/ccps/${ccpId}/link-check`),
            { check_form_id, field_scope }
        );
        this._emit("link-check", planId);
        return result;
    }

    /** Attach an uploaded file (storage_path returned by API.Storage.Create). */
    async AddAttachment(planId, attachment) {
        const result = await this._request("post", this._url(`/${planId}/attachments`), attachment);
        this._emit("attachment-add", planId);
        return result;
    }

    /** Remove an attachment from the plan. */
    async DeleteAttachment(planId, attachmentId) {
        const result = await this._request("delete", this._url(`/${planId}/attachments/${attachmentId}`));
        this._emit("attachment-delete", planId);
        return result;
    }

    /** Download the rendered PDF as a Blob. */
    async ExportPDF(planId) {
        const url = this._url(`/${planId}/export.pdf`);
        try {
            // server-side Gotenberg PDF render — no global timeout
            const response = await axios.get(url, { responseType: "blob", timeout: 0 });
            return response.data;
        } catch (error) {
            throw normalizeAPIError(error);
        }
    }

    /** List available templates. */
    async ListTemplates() {
        return this._request("get", `${getAPIUrlV2("haccp-templates")}`);
    }

    /** Fetch a single template definition. */
    async GetTemplate(templateID) {
        return this._request("get", `${getAPIUrlV2("haccp-templates")}/${templateID}`);
    }
}

// Suppress unused-import warning for getAPIUrl — kept here so callers can
// import alongside the templates URL helper without juggling two paths.
void getAPIUrl;
